import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Search } from 'lucide-react';

import { useLotStore } from '@/stores/useLot.store';

import { Input } from '@/components/shadcn-ui/input';
import Loader from '@/components/sharedComponents/Loader';
import StorageLotCard from '@/components/storage-stage-components/StorageLotCard';

import { useDebounceValue } from '@/hooks/useDebouncedValue';

const LIMIT = 20;

const StorageLotsPage = () => {
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState('');

  const fetchStorageStageLots = useLotStore(state => state.fetchStorageStageLots);
  const storageLots = useLotStore(state => state.storageLots);
  const pagination = useLotStore(state => state.pagination);

  // Refs to avoid re-creating observer
  const observerRef = useRef(null);
  const loadingRef = useRef(false);
  const paginationRef = useRef(pagination);
  const fetchStorageStageLotsRef = useRef(fetchStorageStageLots);
  const queryRef = useRef('');

  /* ---------------- keep refs in sync ---------------- */
  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);

  useEffect(() => {
    fetchStorageStageLotsRef.current = fetchStorageStageLots;
  }, [fetchStorageStageLots]);

  /* ---------------- initial load ---------------- */
  useEffect(() => {
    fetchStorageStageLots();
  }, [fetchStorageStageLots]);

  /* ---------------- search + page 1 fetch ---------------- */
  const debouncedQuery = useDebounceValue(query, 300);

  useEffect(() => {
    queryRef.current = debouncedQuery || '';
    setPage(1);

    loadingRef.current = true;
    setLoading(true);

    fetchStorageStageLotsRef.current(queryRef.current, 1, LIMIT).finally(() => {
      loadingRef.current = false;
      setLoading(false);
    });
  }, [debouncedQuery]);

  /* ---------------- next page fetch ---------------- */
  useEffect(() => {
    if (page === 1) return;
    if (loadingRef.current) return;

    loadingRef.current = true;
    setLoading(true);

    fetchStorageStageLotsRef.current(queryRef.current, page, LIMIT).finally(() => {
      loadingRef.current = false;
      setLoading(false);
    });
  }, [page]);

  /* ---------------- intersection observer ---------------- */
  const observerCallbackRef = useCallback(node => {
    if (observerRef.current) observerRef.current.disconnect();
    if (!node) return;

    observerRef.current = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && !loadingRef.current && paginationRef.current?.hasNextPage) {
          setPage(prev => prev + 1);
        }
      },
      { rootMargin: '150px' }
    );

    observerRef.current.observe(node);
  }, []);

  return (
    <div className="flex h-full flex-col space-y-[18px] overflow-hidden px-4 py-[18px]">
      <div className="flex min-h-0 flex-1 flex-col">
        {/* Search Input (fixed height) */}
        <div className="relative h-11 flex-shrink-0">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search lots..."
            className="h-full rounded-[8px] bg-white pl-9 text-base"
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
        </div>

        <div className="mt-3 flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
          {storageLots?.length > 0 ? (
            <>
              {storageLots.map(lot => (
                <StorageLotCard key={lot._id} lot={lot} />
              ))}

              {pagination?.hasNextPage && <div ref={observerCallbackRef} className="h-4" />}

              {loading && (
                <div className="flex justify-center py-4">
                  <Loader />
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-1 items-center justify-center">
              <p className="text-sm text-muted-foreground">No lots found</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StorageLotsPage;
